/**
 * Chat List Display Cache
 * 
 * Caches computed display data for chat list items to enable instant page loads.
 * After the first visit, the chat list appears immediately from cache while 
 * data refreshes in the background.
 */

import type { Chat, Artist } from '@/types/models'
import { buildChatListPreviewFields, type ChatListMessageReceipt } from '@/lib/chatData'
import type { ChatUserInfo } from './chatData'

export interface CachedChatDisplayData {
  chatId: string
  // Display info
  displayName: string
  displaySecondary: string
  avatarUrl: string | null
  avatarInitial: string
  // Additional user/artist details
  firstName?: string
  lastName?: string
  instagram?: string
  email?: string
  // Message info
  /** Peer preview line incl. age suffix, or empty when row shows Sent/Seen */
  lastMessagePreview: string
  lastMessageAt: number // timestamp
  messageReceipt: ChatListMessageReceipt
  // Status
  unreadCount: number
  hasUnread: boolean
  hasBooking: boolean
  bookingId?: string | null // ID of associated booking if this chat has a booking
  bookingStatus?: string
  // Metadata
  cachedAt: number
  userId?: string // For artist view
  artistId?: string // For user view
}

export interface ChatListCacheData {
  isArtistView: boolean
  items: CachedChatDisplayData[]
  leads: Record<string, boolean> // Simple cache: { chatId: true if has lead }
  cachedAt: number
  version: number // For cache invalidation if structure changes
}

const CACHE_VERSION = 6 // uncensored client display names in artist view
const CACHE_KEY_PREFIX = 'chat_list_cache_'
const CACHE_TTL = 7 * 24 * 60 * 60 * 1000 // 7 days

/**
 * Get the cache key for the current user and view mode
 */
function getCacheKey(userId: string, isArtistView: boolean): string {
  return `${CACHE_KEY_PREFIX}${userId}_${isArtistView ? 'artist' : 'user'}`
}

/**
 * Load cached chat list display data
 */
export function loadChatListCache(userId: string, isArtistView: boolean): CachedChatDisplayData[] | null {
  try {
    const cacheKey = getCacheKey(userId, isArtistView)
    const cached = localStorage.getItem(cacheKey)
    
    if (!cached) {
      return null
    }
    
    const data: ChatListCacheData = JSON.parse(cached)
    
    // Validate cache version
    if (data.version !== CACHE_VERSION) {
      console.log('[ChatListCache] Cache version mismatch, invalidating')
      localStorage.removeItem(cacheKey)
      return null
    }
    
    // Check if cache is expired
    const age = Date.now() - data.cachedAt
    if (age > CACHE_TTL) {
      console.log('[ChatListCache] Cache expired, invalidating')
      localStorage.removeItem(cacheKey)
      return null
    }
    
    console.log('[ChatListCache] Loaded cache with', data.items.length, 'items, age:', Math.round(age / 1000), 'seconds')
    return data.items
  } catch (error) {
    console.error('[ChatListCache] Error loading cache:', error)
    return null
  }
}

/**
 * Save chat list display data to cache
 */
export function saveChatListCache(
  userId: string, 
  isArtistView: boolean, 
  items: CachedChatDisplayData[],
  leads: Record<string, boolean> = {}
): void {
  try {
    const cacheKey = getCacheKey(userId, isArtistView)
    const data: ChatListCacheData = {
      isArtistView,
      items,
      leads, // Cache which chats have leads
      cachedAt: Date.now(),
      version: CACHE_VERSION
    }
    
    localStorage.setItem(cacheKey, JSON.stringify(data))
    console.log('[ChatListCache] Saved cache with', items.length, 'items and', Object.keys(leads).length, 'leads')
  } catch (error) {
    console.error('[ChatListCache] Error saving cache:', error)
    // If localStorage is full, try to clear old caches
    try {
      clearOldCaches()
      localStorage.setItem(getCacheKey(userId, isArtistView), JSON.stringify({
        isArtistView,
        items,
        leads: {},
        cachedAt: Date.now(),
        version: CACHE_VERSION
      }))
    } catch (retryError) {
      console.error('[ChatListCache] Failed to save cache even after cleanup:', retryError)
    }
  }
}

/**
 * Clear all chat list caches for a user
 */
export function clearChatListCache(userId: string): void {
  try {
    localStorage.removeItem(getCacheKey(userId, true))
    localStorage.removeItem(getCacheKey(userId, false))
    console.log('[ChatListCache] Cleared cache for user:', userId)
  } catch (error) {
    console.error('[ChatListCache] Error clearing cache:', error)
  }
}

/**
 * Load and return cached leads information
 */
export function getCachedLeads(userId: string, isArtistView: boolean): Record<string, boolean> {
  try {
    const cacheKey = getCacheKey(userId, isArtistView)
    const cached = localStorage.getItem(cacheKey)
    
    if (!cached) {
      return {}
    }
    
    const data: ChatListCacheData = JSON.parse(cached)
    return data.leads || {}
  } catch (error) {
    console.error('[ChatListCache] Error loading cached leads:', error)
    return {}
  }
}

/**
 * Clear old caches to free up space
 */
function clearOldCaches(): void {
  const keysToRemove: string[] = []
  
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i)
    if (key?.startsWith(CACHE_KEY_PREFIX)) {
      try {
        const cached = localStorage.getItem(key)
        if (cached) {
          const data: ChatListCacheData = JSON.parse(cached)
          const age = Date.now() - data.cachedAt
          
          // Remove caches older than TTL
          if (age > CACHE_TTL) {
            keysToRemove.push(key)
          }
        }
      } catch (error) {
        // Invalid data, mark for removal
        keysToRemove.push(key)
      }
    }
  }
  
  keysToRemove.forEach(key => localStorage.removeItem(key))
  console.log('[ChatListCache] Cleared', keysToRemove.length, 'old caches')
}

/**
 * Compute display data from raw chat data
 */
export function computeChatDisplayData(
  chat: Chat,
  artist: Artist | null,
  user: ChatUserInfo | null,
  isArtistView: boolean,
  unreadCount: number,
  bookingId: string | null,
  bookingStatus: string | undefined,
  currentUserId: string,
): CachedChatDisplayData {
  // Compute display name
  let displayName = 'Unknown'
  let displaySecondary = ''
  let avatarUrl: string | null = null
  let avatarInitial = '?'
  let firstName: string | undefined
  let lastName: string | undefined
  let instagram: string | undefined
  let email: string | undefined
  
  if (isArtistView && user) {
    // Artist view - show user info
    if (user.instagram) {
      const instagramHandle = user.instagram.startsWith('@') ? user.instagram.substring(1) : user.instagram
      displayName = instagramHandle
      instagram = user.instagram
    } else if (user.email) {
      displayName = user.email
      displaySecondary = user.displayName || ''
      email = user.email
    } else {
      displayName = user.displayName || 'User'
    }
    
    avatarUrl = user.avatarUrl || null
    avatarInitial = ((user.instagram || user.email || user.displayName || '?').replace(/^@/, '')[0] || '?').toUpperCase()
  } else if (!isArtistView && artist) {
    // User view - show artist info
    displayName = artist.displayName || 'Unknown'
    firstName = artist.firstName
    lastName = artist.lastName
    avatarUrl = artist.avatarUrl || null
    avatarInitial = (artist.displayName?.[0] || '?').toUpperCase()
  }
  
  const { lastMessagePreview, messageReceipt } = buildChatListPreviewFields(chat, currentUserId)

  return {
    chatId: chat.id,
    displayName,
    displaySecondary,
    avatarUrl,
    avatarInitial,
    firstName,
    lastName,
    instagram,
    email,
    lastMessagePreview,
    lastMessageAt: chat.lastMessageAt.getTime(),
    messageReceipt,
    unreadCount,
    hasUnread: unreadCount > 0,
    hasBooking: !!bookingId,
    bookingId,
    bookingStatus,
    cachedAt: Date.now(),
    userId: chat.userId,
    artistId: chat.artistId
  }
}

